import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtMoney, INVOICE_STATUS_META, type InvoiceStatus } from "@/lib/invoicing";
import { FileText } from "lucide-react";
import { buildSocialMeta, SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/invoice/$token")({
    head: ({ params }) => ({
    meta: [
      ...buildSocialMeta({
        title: "Invoice — CoachQuick",
        description: "View your invoice from your coach.",
        path: `/invoice/${params.token}`,
      }),
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/invoice/${params.token}` }],
  }),
  component: PublicInvoicePage,
});

type PublicInvoice = {
  invoice: {
    id: string;
    invoice_number: string;
    status: InvoiceStatus;
    issue_date: string;
    due_date: string | null;
    paid_date: string | null;
    subtotal_cents: number;
    total_cents: number;
    amount_paid_cents: number;
    currency: string;
    notes: string | null;
    payment_instructions: string | null;
  };
  line_items: Array<{
    id: string;
    description: string;
    quantity: number;
    unit_price_cents: number;
    amount_cents: number;
  }>;
  coach: {
    business_name: string | null;
    business_address: string | null;
    tax_id: string | null;
    invoice_footer: string | null;
    full_name: string | null;
    connected_email: string | null;
    phone: string | null;
    website: string | null;
  };
  client: { name: string | null; email: string | null };
};

function PublicInvoicePage() {
  const { token } = Route.useParams();
  const [data, setData] = useState<PublicInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: res, error } = await supabase.rpc(
        "get_public_invoice" as never,
        { _token: token } as never,
      );
      if (error) setError(error.message);
      else if (!res) setError("Invoice not found.");
      else setData(res as unknown as PublicInvoice);
      setLoading(false);
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary/30 px-4 py-8">
        <Card className="mx-auto max-w-2xl p-8 space-y-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="mt-6 h-32 w-full" />
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary/30 px-4">
        <Card className="w-full max-w-md p-8 text-center">
          <FileText className="mx-auto h-10 w-10 text-muted-foreground/60" />
          <h1 className="mt-3 text-lg font-bold">Invoice not available</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error ?? "This invoice link is invalid or has been removed."}
          </p>
        </Card>
      </div>
    );
  }

  const { invoice: inv, line_items, coach, client } = data;
  const balance = Math.max(inv.total_cents - inv.amount_paid_cents, 0);
  const meta = INVOICE_STATUS_META[inv.status];
  const sender = coach.business_name || coach.full_name || "Your coach";

  return (
    <div className="min-h-screen bg-secondary/30 px-4 py-6 md:py-10">
      <Card className="mx-auto max-w-2xl overflow-hidden">
        <div className="border-b border-border bg-background p-6 md:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Invoice from {sender}</h1>
              {coach.business_address && (
                <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">{coach.business_address}</p>
              )}
              {coach.connected_email && (
                <p className="mt-1 text-xs text-muted-foreground">{coach.connected_email}</p>
              )}
              {coach.phone && (
                <p className="text-xs text-muted-foreground">{coach.phone}</p>
              )}
            </div>
            <div className="text-right">
              <p className="font-mono text-xs text-muted-foreground">{inv.invoice_number}</p>
              <span className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${meta.cls}`}>
                {meta.label}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-6 p-6 md:p-8">
          <div className="grid gap-4 sm:grid-cols-2 text-sm">
            <div>
              <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Bill to</h2>
              <p className="mt-1 font-medium">{client.name ?? "—"}</p>
              {client.email && <p className="text-xs text-muted-foreground">{client.email}</p>}
            </div>
            <div className="sm:text-right">
              <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Issued</h2>
              <p className="mt-1 font-medium">{inv.issue_date}</p>
              {inv.due_date && (
                <>
                  <h2 className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">Due</h2>
                  <p className="font-medium">{inv.due_date}</p>
                </>
              )}
            </div>
          </div>

          <div>
            <h2 className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Line items</h2>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Description</th>
                    <th className="px-3 py-2 text-right font-medium">Qty</th>
                    <th className="px-3 py-2 text-right font-medium">Price</th>
                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {line_items.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-xs text-muted-foreground">No items</td></tr>
                  ) : line_items.map((li) => (
                    <tr key={li.id} className="border-t border-border">
                      <td className="px-3 py-2.5">{li.description}</td>
                      <td className="px-3 py-2.5 text-right text-muted-foreground">{li.quantity}</td>
                      <td className="px-3 py-2.5 text-right text-muted-foreground">{fmtMoney(li.unit_price_cents, inv.currency)}</td>
                      <td className="px-3 py-2.5 text-right font-medium">{fmtMoney(li.amount_cents, inv.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col items-end gap-1 text-sm">
            <div className="flex w-56 justify-between text-muted-foreground">
              <span>Subtotal</span><span>{fmtMoney(inv.subtotal_cents, inv.currency)}</span>
            </div>
            <div className="flex w-56 justify-between font-semibold">
              <span>Total</span><span>{fmtMoney(inv.total_cents, inv.currency)}</span>
            </div>
            {inv.amount_paid_cents > 0 && (
              <div className="flex w-56 justify-between text-success">
                <span>Paid</span><span>{fmtMoney(inv.amount_paid_cents, inv.currency)}</span>
              </div>
            )}
            <div className="mt-2 flex w-56 justify-between rounded-md bg-primary/10 px-2 py-1.5 text-base font-bold text-brand-text">
              <span>Balance due</span><span>{fmtMoney(balance, inv.currency)}</span>
            </div>
          </div>

          {inv.payment_instructions && (
            <div className="rounded-lg bg-primary/5 p-4">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-brand-text">How to pay</h2>
              <p className="mt-1 whitespace-pre-wrap text-sm">{inv.payment_instructions}</p>
            </div>
          )}

          {inv.notes && (
            <div className="text-sm">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</h2>
              <p className="mt-1 whitespace-pre-wrap">{inv.notes}</p>
            </div>
          )}

          {(coach.invoice_footer || coach.tax_id) && (
            <div className="border-t border-border pt-4 text-xs text-muted-foreground">
              {coach.tax_id && <p>Tax ID: {coach.tax_id}</p>}
              {coach.invoice_footer && <p className="mt-1 whitespace-pre-wrap">{coach.invoice_footer}</p>}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
